import React from "react";
import { Sword, Skull, Grid3x3 } from "lucide-react";
import { GameState, PlayerState } from "../../../core/protocol/types";
import { TEAM_NAMES, TEAM_COLORS } from "../../../core/constants";

interface ScoreboardOverlaySurvivorProps {
  gameState: GameState;
  localStats: { score: number; level: number; lines: number };
}

const ScoreboardOverlaySurvivor: React.FC<ScoreboardOverlaySurvivorProps> = ({
  gameState,
  localStats,
}) => {
  const localPlayer = gameState.localConnectionId
    ? gameState.connections.get(gameState.localConnectionId)
    : null;
  const localNumPlayer = localPlayer?.numPlayer;
  const localTeam = localPlayer?.team ?? 0;
  
  // Calculate team stats
  // In survivor mode: Team score = round wins, Player score = frags
  const teamStats = new Map<
    number,
    {
      roundWins: number;
      frags: number;
      deaths: number;
      lines: number;
      players: number;
      alive: number;
    }
  >();

  // Initialize all teams
  for (let i = 0; i < 8; i++) {
    teamStats.set(i, {
      roundWins: 0,
      frags: 0,
      deaths: 0,
      lines: 0,
      players: 0,
      alive: 0,
    });
  }

  // Aggregate stats by team
  gameState.players.forEach((player) => {
    if (player.isDedicatedServer) return;
    const team = player.team ?? 0;
    const stats = teamStats.get(team);
    if (stats) {
      stats.roundWins += player.roundWins || 0;
      stats.frags += player.frags || 0;
      stats.deaths += player.deaths || 0;
      stats.lines += player.lines || 0;
      stats.players += 1;
      if (player.isActive && player.state !== "dead") {
        stats.alive += 1;
      }
      console.log(`[ScoreboardSurvivor] Player ${player.name} - RoundWins: ${player.roundWins}, Frags: ${player.frags}`);
    }
  });

  // Get players grouped by team
  const playersByTeam = new Map<number, PlayerState[]>();
  gameState.players.forEach((player) => {
    if (player.isDedicatedServer) return;
    const team = player.team ?? 0;
    if (!playersByTeam.has(team)) {
      playersByTeam.set(team, []);
    }
    playersByTeam.get(team)!.push(player);
  });

  // Sort players by frags (kills)
  playersByTeam.forEach((players) => {
    players.sort((a, b) => (b.frags || 0) - (a.frags || 0));
  });

  // Get teams that have players, sorted by team ID
  const activeTeams = Array.from(teamStats.entries())
    .filter(([_, stats]) => stats.players > 0)
    .sort(([teamIdA], [teamIdB]) => teamIdA - teamIdB);

  return (
    <div
      className="scoreboard-overlay"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "Tab") {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="scoreboard-overlay-content">
        {activeTeams.map(([teamId, stats]) => {
          const isMyTeam = localTeam === teamId;
          const teamPlayers = playersByTeam.get(teamId) || [];
          const teamColor = TEAM_COLORS[teamId];

          return (
            <div
              key={teamId}
              className={`scoreboard-overlay-team ${isMyTeam ? "my-team" : ""}`}
            >
              <div className="scoreboard-overlay-team-header">
                <div
                  className="scoreboard-overlay-team-score-box"
                  style={{ backgroundColor: teamColor }}
                >
                  <span className="scoreboard-overlay-team-score">
                    {stats.roundWins}
                  </span>
                </div>
                <div className="scoreboard-overlay-team-info">
                  <span className="scoreboard-overlay-team-name">
                    {TEAM_NAMES[teamId]}
                  </span>
                  <span className="scoreboard-overlay-team-alive">
                    Alive: {stats.alive}/{stats.players}
                  </span>
                </div>
              </div>
              <table className="scoreboard-overlay-table">
                <thead>
                  <tr>
                    <th className="scoreboard-col-player">Player</th>
                    <th className="scoreboard-col-stat">
                      <Sword size={12} />
                    </th>
                    <th className="scoreboard-col-stat">
                      <Skull size={12} />
                    </th>
                    <th className="scoreboard-col-stat">
                      <Grid3x3 size={12} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamPlayers.map((player) => {
                    // Use numPlayer for consistency with game logic (matches original design)
                    const isLocalPlayer = localNumPlayer !== undefined && player.numPlayer === localNumPlayer;
                    const isAlive = player.isActive && player.state !== "dead";
                    return (
                      <tr
                        key={player.connectionId}
                        className={`scoreboard-overlay-player-row ${
                          isLocalPlayer ? "local-player" : ""
                        } ${!isAlive ? "dead" : ""}`}
                      >
                        <td className="scoreboard-col-player">
                          <span
                            className="scoreboard-overlay-player-name"
                            style={{
                              opacity: player.isGone ? 0.6 : 1,
                              color: player.isGone
                                ? "rgba(128, 128, 128, 0.8)"
                                : undefined,
                            }}
                          >
                            {player.name}
                            {player.isGone ? " *" : ""}
                            {isLocalPlayer && (
                              <span className="scoreboard-overlay-you-badge">
                                You
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="scoreboard-col-stat">
                          {player.frags || 0}
                        </td>
                        <td className="scoreboard-col-stat">
                          {player.deaths || 0}
                        </td>
                        <td className="scoreboard-col-stat">
                          {player.lines || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <style>{`
        .scoreboard-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(8px);
          z-index: 3000;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.1s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .scoreboard-overlay-content {
          width: 90%;
          max-width: 900px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          gap: 20px;
          overflow-y: auto;
          padding: 20px;
        }

        .scoreboard-overlay-team {
          background: rgba(20, 25, 35, 0.95);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          overflow: hidden;
        }

        .scoreboard-overlay-team.my-team {
          border-color: rgba(255, 215, 0, 0.5);
          border-width: 2px;
        }

        .scoreboard-overlay-team-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px 16px;
          background: rgba(0, 0, 0, 0.3);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .scoreboard-overlay-team-score-box {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .scoreboard-overlay-team-score {
          font-size: 24px;
          font-weight: 700;
          color: white;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
        }

        .scoreboard-overlay-team-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .scoreboard-overlay-team-name {
          font-size: 16px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.95);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .scoreboard-overlay-team-alive {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          font-weight: 500;
        }

        .scoreboard-overlay-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .scoreboard-overlay-table thead {
          background: rgba(0, 0, 0, 0.2);
        }

        .scoreboard-overlay-table th {
          padding: 8px 12px;
          text-align: left;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.7);
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .scoreboard-overlay-table th.scoreboard-col-stat {
          text-align: right;
        }

        .scoreboard-overlay-table th.scoreboard-col-stat svg {
          opacity: 0.8;
          color: rgba(255, 255, 255, 0.6);
        }

        .scoreboard-overlay-table td {
          padding: 6px 12px;
          color: rgba(255, 255, 255, 0.9);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .scoreboard-overlay-player-row {
          transition: background-color 0.1s ease;
        }

        .scoreboard-overlay-player-row:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .scoreboard-overlay-player-row.local-player {
          background: rgba(255, 215, 0, 0.15);
          font-weight: 600;
        }

        .scoreboard-overlay-player-row.local-player:hover {
          background: rgba(255, 215, 0, 0.2);
        }

        .scoreboard-overlay-player-row.dead {
          opacity: 0.5;
        }

        .scoreboard-overlay-player-row.dead .scoreboard-overlay-player-name {
          text-decoration: line-through;
        }

        .scoreboard-col-player {
          width: auto;
          min-width: 200px;
        }

        .scoreboard-col-stat {
          width: 80px;
          text-align: right;
          font-family: 'Courier New', 'Monaco', monospace;
          font-weight: 600;
        }

        .scoreboard-overlay-player-name {
          display: flex;
          align-items: center;
          gap: 8px;
          color: rgba(255, 255, 255, 0.95);
        }

        .scoreboard-overlay-you-badge {
          font-size: 9px;
          padding: 2px 6px;
          background: rgba(255, 215, 0, 0.25);
          border: 1px solid rgba(255, 215, 0, 0.4);
          border-radius: 2px;
          color: rgba(255, 215, 0, 1);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      `}</style>
    </div>
  );
};

export default ScoreboardOverlaySurvivor;

